import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { historyImportAPI, customerAPI } from '../services/api';
import {
  Upload, ArrowLeft, CheckCircle, AlertTriangle, Loader, FileSpreadsheet,
  Search, Trash2, Link2, RefreshCw, Sparkles, Zap, Download,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

const VALIDATE_CHUNK = 1000;  // matches backend /history-import/validate cap
const RUN_BATCH = 500;        // matches backend /history-import/run cap
const BULK_LINK_BATCH = 200;  // matches backend /history-import/pending/bulk-link-perfect default

// mode must be one of these (case-insensitive) — anything else falls back to 'adjustment'
// on the backend (see MODE_MAP in routes/historyImport.js). Shared by both services.
const VALID_MODES = ['CASH', 'UPI', 'GOOGLE PAY', 'BHIM', 'BANK_TRANSFER', 'CHEQUE', 'PORTAL', 'COUPON', 'OTHERS', 'BIX42'];

// Column names must match `normalizeRow()` in backend/src/routes/historyImport.js exactly —
// these are the raw xlsx headers the backend reads (rec.payment_id, rec['Account No.'] /
// rec.Username, etc.), not the internal camelCase field names used after normalization.
// Same shape as SyncImportPage's SERVICES config: one object per service holding
// presentational strings + the field/template/column-guide data, nothing else in the
// component branches on `service` directly.
const SERVICES = {
  catv: {
    title: 'Payment History Import',
    subtitle: 'Bulk-load historical CableTV payment records (e.g. HI.xlsx). Matches by customer address; anything unmatched goes to the queue below for manual linking.',
    templateFilename: 'payment-history-import-template.xlsx',
    templateColumns: [
      'payment_id', 'date', 'name', 'Society', 'address', 'mobile', 'record_timestamp',
      'Account No.', 'previous_balance', 'paid_amount', 'discount_amount', 'balance_amount',
      'current_balance', 'comment', 'mode', 'collected_by', 'stb_no', 'card_no', 'stb_name',
    ],
    templateSampleRows: [
      {
        payment_id: 1001, date: '2026-01-15', name: 'Ramesh Patel', Society: 'Shreeji Society',
        address: '12, Shreeji Society, Ahmedabad', mobile: '9876543210',
        record_timestamp: '2026-01-15 10:30:00', 'Account No.': 'AC12345',
        previous_balance: 500, paid_amount: 300, discount_amount: 0, balance_amount: 200,
        current_balance: 200, comment: 'Monthly cable payment', mode: 'CASH',
        collected_by: 'Agent Name', stb_no: 'STB-001', card_no: 'CARD-001', stb_name: 'HD Box',
      },
      {
        payment_id: 1002, date: '2026-01-16', name: 'Sunita Shah', Society: '',
        address: '45, Gandhi Road, Ahmedabad', mobile: '9123456780',
        record_timestamp: '2026-01-16 14:05:00', 'Account No.': 'AC12346',
        previous_balance: 0, paid_amount: 350, discount_amount: 0, balance_amount: 0,
        current_balance: 0, comment: '', mode: 'UPI',
        collected_by: '', stb_no: '', card_no: '', stb_name: '',
      },
    ],
    columnNotes: [
      ['Column', 'Required', 'Notes'],
      ['payment_id', 'Yes', 'Unique per row — used to skip duplicates on re-upload'],
      ['date', 'No', 'Format: YYYY-MM-DD'],
      ['name', 'No', 'Customer name as it appears in the old system'],
      ['Society', 'No', ''],
      ['address', 'No', 'Used to auto-match against Customer.address.line1'],
      ['mobile', 'No', ''],
      ['record_timestamp', 'No', 'Format: YYYY-MM-DD HH:mm:ss'],
      ['Account No.', 'No', 'Old system account number, if known'],
      ['previous_balance', 'No', 'Numeric'],
      ['paid_amount', 'No', 'Numeric — the actual payment amount'],
      ['discount_amount', 'No', 'Numeric'],
      ['balance_amount', 'No', 'Numeric'],
      ['current_balance', 'No', 'Numeric'],
      ['comment', 'No', ''],
      ['mode', 'No', `One of: ${VALID_MODES.join(', ')} (anything else is imported as "adjustment")`],
      ['collected_by', 'No', 'Original collector name — informational only, not linked to a User'],
      ['stb_no', 'No', ''],
      ['card_no', 'No', ''],
      ['stb_name', 'No', ''],
    ],
    extraColumnLabel: 'Society',
    extraColumnValue: (row) => row.society,
  },
  broadband: {
    title: 'Payment History Import — Broadband',
    subtitle: 'Bulk-load historical Broadband payment records. Matches by customer address; anything unmatched goes to the queue below for manual linking.',
    templateFilename: 'payment-history-import-broadband-template.xlsx',
    templateColumns: [
      'payment_id', 'date', 'name', 'address', 'mobile', 'record_timestamp',
      'Username', 'previous_balance', 'paid_amount', 'discount_amount', 'balance_amount',
      'current_balance', 'comment', 'mode', 'collected_by', 'plan_name',
    ],
    templateSampleRows: [
      {
        payment_id: 2001, date: '2026-01-15', name: 'Ramesh Patel',
        address: '12, Shreeji Society, Ahmedabad', mobile: '9876543210',
        record_timestamp: '2026-01-15 10:30:00', Username: 'ramesh_pppoe',
        previous_balance: 500, paid_amount: 599, discount_amount: 0, balance_amount: 0,
        current_balance: 0, comment: 'Monthly broadband payment', mode: 'CASH',
        collected_by: 'Agent Name', plan_name: '100 Mbps Unlimited',
      },
      {
        payment_id: 2002, date: '2026-01-16', name: 'Sunita Shah',
        address: '45, Gandhi Road, Ahmedabad', mobile: '9123456780',
        record_timestamp: '2026-01-16 14:05:00', Username: 'sunita_pppoe',
        previous_balance: 0, paid_amount: 799, discount_amount: 0, balance_amount: 0,
        current_balance: 0, comment: '', mode: 'UPI',
        collected_by: '', plan_name: '200 Mbps Unlimited',
      },
    ],
    columnNotes: [
      ['Column', 'Required', 'Notes'],
      ['payment_id', 'Yes', 'Unique per row — used to skip duplicates on re-upload (must not collide with a CATV import\'s payment_id)'],
      ['date', 'No', 'Format: YYYY-MM-DD'],
      ['name', 'No', 'Customer name as it appears in the old system'],
      ['address', 'No', 'Used to auto-match against Customer.address.line1'],
      ['mobile', 'No', ''],
      ['record_timestamp', 'No', 'Format: YYYY-MM-DD HH:mm:ss'],
      ['Username', 'No', 'PPPoE username, if known — used for suggested-match scoring'],
      ['previous_balance', 'No', 'Numeric'],
      ['paid_amount', 'No', 'Numeric — the actual payment amount'],
      ['discount_amount', 'No', 'Numeric'],
      ['balance_amount', 'No', 'Numeric'],
      ['current_balance', 'No', 'Numeric'],
      ['comment', 'No', ''],
      ['mode', 'No', `One of: ${VALID_MODES.join(', ')} (anything else is imported as "adjustment")`],
      ['collected_by', 'No', 'Original collector name — informational only, not linked to a User'],
      ['plan_name', 'No', ''],
    ],
    extraColumnLabel: 'Username',
    extraColumnValue: (row) => row.accountNoRaw,
  },
};

export function parseHistoryFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (rows.length === 0) return reject(new Error('No data rows found in the file'));
        resolve(rows);
      } catch (err) {
        reject(new Error(`Cannot parse file: ${err.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

function downloadHistoryTemplate(cfg) {
  const wb = XLSX.utils.book_new();
  const wsData = XLSX.utils.json_to_sheet(cfg.templateSampleRows, { header: cfg.templateColumns });
  XLSX.utils.book_append_sheet(wb, wsData, 'History');
  const wsNotes = XLSX.utils.aoa_to_sheet(cfg.columnNotes);
  XLSX.utils.book_append_sheet(wb, wsNotes, 'Column Guide');
  XLSX.writeFile(wb, cfg.templateFilename);
}

// ── One row of the Pending Links review queue — owns its own customer search ──
function PendingRow({ row, onResolved, service, cfg }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!search.trim() || search.length < 2) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await customerAPI.getAll({ search, limit: 8 });
        setResults(data.data || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [search]);

  const link = async (customer) => {
    setBusy(true);
    try {
      await historyImportAPI.linkPending(row._id, customer._id, service);
      toast.success(`Linked to ${customer.name}`);
      onResolved(row._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Link failed');
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!window.confirm(`Discard this history row for "${row.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await historyImportAPI.discardPending(row._id, service);
      toast.success('Discarded');
      onResolved(row._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Discard failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td style={{ whiteSpace: 'nowrap' }}>{row.date ? String(row.date).slice(0, 10) : '—'}</td>
      <td>{row.name}</td>
      <td>{cfg.extraColumnValue(row)}</td>
      <td style={{ maxWidth: 220 }}>{row.address}</td>
      <td>{row.mobile}</td>
      <td style={{ textAlign: 'right' }}>
        ₹{fmt(row.paidAmount)}
        {row.importMode === 'live' && (
          <div style={{ fontSize: '.62rem', fontWeight: 700, color: 'var(--c-065f46)', marginTop: 2 }}>LIVE</div>
        )}
      </td>
      <td>{row.mode}</td>
      <td style={{ minWidth: 240 }}>
        <div style={{ position: 'relative' }}>
          {row.suggestions?.length > 0 && (
            <div style={{
              marginBottom: 6, border: '1px solid var(--border)', borderRadius: 8,
              overflow: 'hidden', background: 'var(--card-bg)',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px',
                background: 'var(--bg)', borderBottom: '1px solid var(--border)',
                fontSize: '.66rem', fontWeight: 700, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '.03em',
              }}>
                <Sparkles size={11} />
                Suggested matches
              </div>
              {row.suggestions.map((s, i) => {
                const tone = s.score >= 80
                  ? { text: 'var(--c-065f46)', bar: 'var(--cb-10b981)' }
                  : s.score >= 50
                  ? { text: 'var(--c-92400e)', bar: 'var(--cb-f59e0b)' }
                  : { text: 'var(--c-6b7280)', bar: 'var(--cb-9ca3af)' };
                return (
                  <button
                    key={s.customer._id}
                    type="button"
                    disabled={busy}
                    onClick={() => link(s.customer)}
                    title={s.reasons.join(' · ')}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', border: 'none',
                      borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                      background: 'transparent', padding: '6px 8px',
                      cursor: busy ? 'default' : 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 6 }}>
                      <span style={{
                        fontWeight: 600, fontSize: '.78rem', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{s.customer.name}</span>
                      <span style={{ fontWeight: 700, fontSize: '.72rem', color: tone.text, flexShrink: 0 }}>
                        {s.score}%
                      </span>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', marginTop: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${s.score}%`, background: tone.bar, borderRadius: 2 }} />
                    </div>
                    <div style={{
                      fontSize: '.68rem', color: 'var(--text-muted)', marginTop: 3,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {s.customer.customerId} · {s.reasons.join(', ')}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <div className="search-input-wrapper">
            <Search size={13} className="search-icon" />
            <input
              className="search-input"
              placeholder="Search customer to link…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              disabled={busy}
              autoComplete="off"
            />
          </div>
          {(results.length > 0 || searching) && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
              background: 'var(--card-bg)', border: '1px solid var(--border)',
              borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.15)', marginTop: 4,
            }}>
              {searching ? (
                <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.8rem' }}>Searching…</div>
              ) : (
                results.map(c => (
                  <div key={c._id} onClick={() => link(c)}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: 600, fontSize: '.85rem' }}>{c.name}</div>
                    <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>
                      {c.customerId} · {c.mobile} · {c.address?.line1}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </td>
      <td>
        <button className="btn btn-secondary btn-sm" disabled={busy} onClick={discard} title="Discard">
          <Trash2 size={13} />
        </button>
      </td>
    </tr>
  );
}

export default function HistoryImportPage({ service = 'catv' }) {
  useDarkReady();
  const cfg = SERVICES[service];
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [parsing, setParsing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validateProgress, setValidateProgress] = useState({ done: 0, total: 0 });
  const [validateSummary, setValidateSummary] = useState(null);

  const [records, setRecords] = useState(null); // raw parsed rows, held until "Run Import" confirmed
  // 'historical' = pure backfill, never touches balance/expiry (original behavior).
  // 'live' = treat every row as a real payment happening now — reduces outstandingBalance
  // and extends the service's expiry, same as a manual renewal — for uploading ongoing/
  // current collections until a payment gateway is wired up.
  const [importMode, setImportMode] = useState('historical');

  const [running, setRunning] = useState(false);
  const [runProgress, setRunProgress] = useState({ done: 0, total: 0 });
  const [runResults, setRunResults] = useState(null);

  const [lastImport, setLastImport] = useState(null);

  const [pending, setPending] = useState([]);
  const [pendingPagination, setPendingPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [pendingSearch, setPendingSearch] = useState('');
  const [onlyMatched, setOnlyMatched] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [bulkLinking, setBulkLinking] = useState(false);
  const [bulkLinkCount, setBulkLinkCount] = useState(0);

  const loadPending = useCallback(async (page = 1, search = pendingSearch, hasMatch = onlyMatched) => {
    setPendingLoading(true);
    try {
      const { data } = await historyImportAPI.getPending({ page, limit: 20, search, hasMatch: hasMatch ? 'true' : undefined }, service);
      setPending(data.data || []);
      setPendingPagination(data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      toast.error('Failed to load pending links');
    } finally {
      setPendingLoading(false);
    }
  }, [pendingSearch, onlyMatched, service]);

  const loadLastImport = useCallback(async () => {
    try {
      const { data } = await historyImportAPI.getLastImport(service);
      setLastImport(data.data || null);
    } catch (err) {
      // non-critical — just don't show the banner
    }
  }, [service]);

  // Reset all upload/validate/run state when switching service (CableTV <-> Broadband
  // route change re-mounts with a different `service` prop) so nothing from one bleeds
  // into the other, then reload that service's own Pending Links queue.
  useEffect(() => {
    setRecords(null);
    setValidateSummary(null);
    setRunResults(null);
    loadPending(1, '', false);
    loadLastImport();
    setPendingSearch('');
    setOnlyMatched(false);
  }, [service]);

  useEffect(() => { loadPending(1, pendingSearch, onlyMatched); }, [onlyMatched]);

  const onRowResolved = (id) => {
    setPending(prev => prev.filter(r => r._id !== id));
    setPendingPagination(p => ({ ...p, total: Math.max(0, p.total - 1) }));
  };

  // Loops the bulk-link-perfect batches (backend caps each call at BULK_LINK_BATCH rows)
  // until a call reports 0 newly linked, meaning no perfect matches remain in the backlog.
  const runBulkLinkPerfect = async () => {
    if (!window.confirm(
      'Automatically link every pending row with a 100% match to its suggested customer?\n\n' +
      'This creates real payment records immediately — it does not go through a review step. ' +
      'Rows below 100% are left untouched for manual review.'
    )) return;

    setBulkLinking(true);
    setBulkLinkCount(0);
    let totalLinked = 0, totalFailed = 0;
    try {
      for (let i = 0; i < 100; i++) { // safety cap on iterations, not expected to be hit
        const { data } = await historyImportAPI.bulkLinkPerfect({ limit: BULK_LINK_BATCH }, service);
        totalLinked += data.data.linked;
        totalFailed += data.data.failed;
        setBulkLinkCount(totalLinked);
        if (data.data.linked === 0) break;
      }
      if (totalLinked > 0) {
        toast.success(`Linked ${totalLinked} payment${totalLinked === 1 ? '' : 's'} automatically${totalFailed ? ` (${totalFailed} failed)` : ''}`);
      } else {
        toast('No 100% matches found to link', { icon: 'ℹ️' });
      }
      loadPending(1, pendingSearch, onlyMatched);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk link failed');
    } finally {
      setBulkLinking(false);
    }
  };

  const handleFile = async (file) => {
    setParsing(true);
    setValidateSummary(null);
    setRunResults(null);
    setRecords(null);
    try {
      const rows = await parseHistoryFile(file);
      setRecords(rows);
      toast.success(`Parsed ${rows.length} rows — click "Check File" to see the match breakdown`);
    } catch (err) {
      toast.error(err.message || 'Could not parse file');
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const runValidate = async () => {
    if (!records || records.length === 0) return;
    setValidating(true);
    setValidateProgress({ done: 0, total: records.length });
    const summary = { autoMatch: 0, ambiguous: 0, noMatch: 0, alreadyImported: 0, total: 0 };
    try {
      for (let i = 0; i < records.length; i += VALIDATE_CHUNK) {
        const chunk = records.slice(i, i + VALIDATE_CHUNK);
        const { data } = await historyImportAPI.validate({ records: chunk }, service);
        summary.autoMatch += data.data.autoMatch;
        summary.ambiguous += data.data.ambiguous;
        summary.noMatch += data.data.noMatch;
        summary.alreadyImported += data.data.alreadyImported;
        summary.total += data.data.total;
        setValidateProgress({ done: Math.min(i + VALIDATE_CHUNK, records.length), total: records.length });
      }
      setValidateSummary(summary);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const runImport = async () => {
    if (!records || records.length === 0) return;
    const liveWarning = importMode === 'live'
      ? '\n\nLive mode: matched rows will REDUCE customer balances and EXTEND their expiry date, same as a real payment.'
      : '';
    if (!window.confirm(`Import ${records.length} history rows now? Matched rows create payment records immediately; unmatched rows go to the Pending Links queue below.${liveWarning}`)) return;

    setRunning(true);
    setRunProgress({ done: 0, total: records.length });
    const totals = { imported: 0, pendingCreated: 0, alreadyImported: 0, ambiguous: 0, failed: 0 };
    try {
      for (let i = 0; i < records.length; i += RUN_BATCH) {
        const batch = records.slice(i, i + RUN_BATCH);
        const { data } = await historyImportAPI.run({ records: batch, importMode }, service);
        totals.imported += data.data.imported;
        totals.pendingCreated += data.data.pendingCreated;
        totals.alreadyImported += data.data.alreadyImported;
        totals.ambiguous += data.data.ambiguous;
        totals.failed += data.data.failed;
        setRunProgress({ done: Math.min(i + RUN_BATCH, records.length), total: records.length });
      }
      setRunResults(totals);
      toast.success(`Imported ${totals.imported} payments, ${totals.pendingCreated + totals.ambiguous} sent to Pending Links`);
      loadPending(1, '');
      loadLastImport();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Import failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div className="flex items-center gap-3 mb-6">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/customers')}>
          <ArrowLeft size={14} />
        </button>
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{cfg.title}</h2>
          <p className="text-muted text-small" style={{ margin: 0 }}>{cfg.subtitle}</p>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}><FileSpreadsheet size={15} style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--primary)' }} />Upload File</h3>
          <span className="text-muted text-small">
            {lastImport
              ? <>Last import: {new Date(lastImport.runAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}{lastImport.runBy?.name ? ` by ${lastImport.runBy.name}` : ''}</>
              : 'No import run yet'}
          </span>
        </div>
        <div className="card-body">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
          />

          <div style={{
            display: 'flex', gap: 10, marginBottom: 14, padding: 10,
            border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg)',
          }}>
            {[
              { value: 'historical', label: 'Historical backfill', desc: 'Old records only — never touches balance or expiry' },
              { value: 'live', label: 'Live payment entry', desc: 'Real payments happening now — reduces balance & extends expiry, like a manual renewal' },
            ].map(opt => (
              <label key={opt.value} style={{
                flex: 1, display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer',
                padding: 8, borderRadius: 6,
                border: importMode === opt.value ? '1px solid var(--primary)' : '1px solid transparent',
                background: importMode === opt.value ? 'var(--card-bg)' : 'transparent',
              }}>
                <input
                  type="radio"
                  name="importMode"
                  checked={importMode === opt.value}
                  onChange={() => setImportMode(opt.value)}
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '.85rem' }}>{opt.label}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" disabled={parsing} onClick={() => fileRef.current?.click()}>
              {parsing ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={14} />}
              {parsing ? 'Parsing…' : 'Choose File'}
            </button>
            <button className="btn btn-secondary" onClick={() => downloadHistoryTemplate(cfg)}>
              <Download size={14} /> Download Template
            </button>
          </div>

          {records && (
            <div style={{ marginTop: 16 }}>
              <p style={{ fontSize: '0.85rem', margin: '0 0 12px' }}>{records.length} rows parsed.</p>
              <div className="flex items-center gap-3 mb-3">
                <button className="btn btn-secondary btn-sm" disabled={validating} onClick={runValidate}>
                  {validating ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={13} />}
                  {validating ? `Checking ${validateProgress.done}/${validateProgress.total}…` : 'Check File'}
                </button>
                <button className="btn btn-primary btn-sm" disabled={running} onClick={runImport}>
                  {running ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Link2 size={13} />}
                  {running ? `Importing ${runProgress.done}/${runProgress.total}…` : 'Run Import'}
                </button>
              </div>

              {validateSummary && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <SummaryTile label="Will auto-import" value={validateSummary.autoMatch} color="var(--c-065f46)" bg="var(--cb-d1fae5)" />
                  <SummaryTile label="Ambiguous address" value={validateSummary.ambiguous} color="var(--c-92400e)" bg="var(--cb-fef3c7)" />
                  <SummaryTile label="No address match" value={validateSummary.noMatch} color="var(--c-92400e)" bg="var(--cb-fef3c7)" />
                  <SummaryTile label="Already imported" value={validateSummary.alreadyImported} color="var(--c-374151)" bg="var(--cb-e5e7eb)" />
                </div>
              )}

              {runResults && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                  <SummaryTile label="Imported" value={runResults.imported} color="var(--c-065f46)" bg="var(--cb-d1fae5)" />
                  <SummaryTile label="Sent to Pending Links" value={runResults.pendingCreated + runResults.ambiguous} color="var(--c-92400e)" bg="var(--cb-fef3c7)" />
                  <SummaryTile label="Already imported" value={runResults.alreadyImported} color="var(--c-374151)" bg="var(--cb-e5e7eb)" />
                  {runResults.failed > 0 && <SummaryTile label="Failed" value={runResults.failed} color="var(--c-991b1b)" bg="var(--cb-fee2e2)" />}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}><AlertTriangle size={15} style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--c-92400e)' }} />Pending Links ({pendingPagination.total})</h3>
          <div className="flex items-center gap-3" style={{ flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" disabled={bulkLinking} onClick={runBulkLinkPerfect} title="Auto-link every pending row whose top suggestion is a 100% match">
              {bulkLinking ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={13} />}
              {bulkLinking ? `Linking… ${bulkLinkCount} done` : 'Link 100% customers'}
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.8rem', color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={onlyMatched}
                onChange={e => { setOnlyMatched(e.target.checked); }}
              />
              <Sparkles size={13} />
              Only show rows with a match
            </label>
            <div className="search-input-wrapper" style={{ maxWidth: 260 }}>
              <Search size={13} className="search-icon" />
              <input
                className="search-input"
                placeholder="Search name, address, mobile…"
                value={pendingSearch}
                onChange={e => setPendingSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') loadPending(1, pendingSearch, onlyMatched); }}
              />
            </div>
          </div>
        </div>
        <div className="card-body" style={{ overflowX: 'auto' }}>
          {pendingLoading ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /></div>
          ) : pending.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
              <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />No pending rows to review.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                  <th>Date</th><th>Name</th><th>{cfg.extraColumnLabel}</th><th>Address</th><th>Mobile</th>
                  <th style={{ textAlign: 'right' }}>Amount</th><th>Mode</th><th>Link to customer</th><th></th>
                </tr>
              </thead>
              <tbody>
                {pending.map(row => <PendingRow key={row._id} row={row} onResolved={onRowResolved} service={service} cfg={cfg} />)}
              </tbody>
            </table>
          )}

          {pendingPagination.pages > 1 && (
            <div className="flex items-center gap-3 mt-3">
              <span className="text-muted text-small">Page {pendingPagination.page} of {pendingPagination.pages} · {pendingPagination.total} total</span>
              <button className="page-btn" disabled={pendingPagination.page <= 1} onClick={() => loadPending(pendingPagination.page - 1, pendingSearch)}>←</button>
              <button className="page-btn" disabled={pendingPagination.page >= pendingPagination.pages} onClick={() => loadPending(pendingPagination.page + 1, pendingSearch)}>→</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, color, bg }) {
  return (
    <div style={{ background: bg, color, borderRadius: 8, padding: '10px 16px', minWidth: 140 }}>
      <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: '0.75rem' }}>{label}</div>
    </div>
  );
}
